import React, { useEffect, useState } from 'react';
import '../../styles/dashboard.css';
import '../../styles/register.css';
import useAxiosPrivate from '../../api/useAxiosPrivate';
import { notify } from '../toast';
import Modal from 'react-modal';
import { useNavigate } from 'react-router-dom';
import imageCompression from "browser-image-compression";

const HospitalDashboard = ({ user }) => {

    const admittedAtOptions = {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true
    };

    const [photo, setPhoto] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const [doctors, setDoctors] = useState([]);
    const [patients, setPatients] = useState([]);
    const [totalPatients, setTotalPatients] = useState(1);
    const [loadPatients, setLoadPatients] = useState(true);

    const [name, setName] = useState('');
    const [speciality, setSpeciality] = useState('');

    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(false);
    const [openDoctor, setOpenDoctor] = useState(false);
    const [openPhotoUpload, setOpenPhotoUpload] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [deleteModalData, setDeleteModalData] = useState({});

    const [cards, setCards] = useState([
        { title: "Total patients", value: 0 },
        { title: "Total doctors", value: 0 },
    ]);

    const [filter, setFilter] = useState('');
    const [filteredPatients, setFilteredPatients] = useState([]);

    const axiosPrivate = useAxiosPrivate();
    const navigate = useNavigate();

    useEffect(() => {
        if (loadPatients)
            fetchHospitalDashboard();
        fetchPhoto();

        // eslint-disable-next-line
    }, [loadPatients]);

    const fetchPhoto = async () => {
        try {
            const response = await axiosPrivate.get("/hospital/getPhoto");

            if (response?.data?.photo) setPhoto(`data:image/jpeg;base64,${bufferToBase64(response?.data?.photo?.data)}`);

        } catch (err) {
            notify('failed', err?.response?.data?.message);
        }
    }

    const fetchHospitalDashboard = async () => {
        try {

            if ((totalPatients === patients.length) && (loadPatients)) {
                notify('info', 'End of list reached');
                setLoadPatients(false);
                return
            }

            let response1;
            if (loadPatients) {
                setPageLoading(true);
                response1 = await axiosPrivate.get(`/hospital/getPatients?page=${Math.ceil(patients.length / 10) + 1}`);
                setPatients(prev => [...prev, ...response1?.data?.patients]);
                setTotalPatients(response1?.data?.total);
            }

            const response2 = await axiosPrivate.get("/hospital/getDoctors");
            setDoctors(response2?.data);

            setCards([
                { title: "Total patients", value: response1?.data?.total || patients.length },
                { title: "Total doctors", value: response2?.data?.length },
            ])

        } catch (err) {
            notify('failed', err?.response?.data?.message);
        } finally {
            setLoadPatients(false);
            setPageLoading(false);
        }
    }

    const fetchFilteredPatients = async () => {
        if (!filter) {
            return clearFilter();
        }

        try {
            const result = await axiosPrivate.get(`/hospital/getFilteredPatients?filter=${filter}`);
            setFilteredPatients(result?.data?.patients);

        } catch (err) {
            notify('failed', err?.response?.data?.message);
            clearFilter();
        }
    };

    const clearFilter = () => {
        setFilter('');
        setFilteredPatients([]);
    }

    const handleAddMember = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            const response = await axiosPrivate.post('/hospital/addDoctor', {
                name,
                speciality
            });
            const res = response?.data;
            notify('success', res?.success);

            fetchHospitalDashboard();

        } catch (err) {
            const errorMessage = err?.response?.data?.message || err?.message || 'error fetching server information';
            notify('failed', errorMessage);
        }
        finally {
            setLoading(false);
            setOpenDoctor(false);
            setName('');
            setSpeciality('');
        }
    }

    const handleDeletePatient = async (id) => {
        try {
            const response = await axiosPrivate.delete(`/hospital/deletePatient/${id}`);
            const res = response?.data;
            notify('success', res?.success);

            setPatients([]);
            setLoadPatients(true);

        } catch (err) {
            const errorMessage = err?.response?.data?.message || err?.message || 'error fetching server information';
            notify('failed', errorMessage);
        }
    }

    const handleDeleteDoctor = async (id) => {
        try {
            const response = await axiosPrivate.delete(`/hospital/deleteDoctor/${id}`);
            const res = response?.data;
            notify('success', res?.success);

            fetchHospitalDashboard();

        } catch (err) {
            const errorMessage = err?.response?.data?.message || err?.message || 'error fetching server information';
            notify('failed', errorMessage);
        }
    }

    const handlePhoto = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);

            const photo = document.getElementById("photo");
            const fd = new FormData();

            // @ts-ignore
            if (!photo?.files[0]) {
                notify('info', 'Please select image before uploading')
                return
            }

            // @ts-ignore
            const compressedImage = await compressImage(photo?.files[0])
            fd.append('photo', compressedImage);

            const response = await axiosPrivate.post('/hospital/uploadPhoto', fd,
                {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });

            const success = response?.data?.success;
            if (success)
                notify('success', success);

            fetchPhoto()
        } catch (err) {
            notify('failed', err?.response?.data?.message);
        } finally {
            setLoading(false);
            setOpenPhotoUpload(false);
        }
    }

    const bufferToBase64 = (bufferArray) => {
        const chunkSize = 100000;
        let base64String = '';

        for (let i = 0; i < bufferArray.length; i += chunkSize) {
            const chunk = bufferArray.slice(i, i + chunkSize);
            base64String += String.fromCharCode.apply(null, chunk);
        }

        return btoa(base64String);
    }

    const compressImage = async (file) => {
        const options = {
            maxSizeMB: 0.048,
            maxWidthOrHeight: 600,
            useWebWorker: true,
        };

        const compressedFile = await imageCompression(file, options);
        return compressedFile;
    };

    return (
        <div className="container">
            <div className="dashboard">
                <div
                    className="inner"
                    style={{
                        display: 'flex',
                        justifyContent: 'space-around',
                        alignItems: 'center'
                    }}>

                    <div>
                        <h2>Welcome back, <i style={{ color: 'var(--primary-text-color)' }}>{user}</i></h2>

                        <div className="cards-container">

                            {cards.map((card, index) => (
                                <div
                                    key={index}
                                    className={`card ${activeIndex === index ? "active" : ""}`}
                                    onClick={() => setActiveIndex(index)}
                                >
                                    <div className="card-body">
                                        <h3 className="card-title">{card.title}</h3>
                                        <h3 className="card-value">{card.value}</h3>
                                    </div>
                                </div>
                            ))}

                        </div>
                    </div>

                    <div className="profile-container">
                        {photo ? (
                            <>
                                <img className="profile" src={photo} alt="" onClick={() => setOpenPhotoUpload(true)} />
                                <p>click the image to change it</p>
                            </>
                        ) :
                            <button className='add-btn' onClick={() => setOpenPhotoUpload(true)}>Upload Photo</button>
                        }
                    </div>

                </div>
            </div>

            <Modal
                isOpen={openPhotoUpload}
                onRequestClose={() => setOpenPhotoUpload(false)}
                style={{
                    overlay: { backgroundColor: "rgba(0,0,0,0.4)" },
                }}
                className='formContainer'
            >
                <div className='formWrapper'>
                    <h2 className='formTitle'>Hospital photo upload</h2>
                    <form onSubmit={handlePhoto}>

                        <div className="formContainer" style={{ marginBottom: '10px' }}>
                            {photo && <img src={photo} height={'100'} alt='hospital-image' />}
                        </div>

                        <div className="formGroup">
                            <input
                                className="formInput"
                                type="file"
                                id="photo"
                                name='photo'
                            />
                        </div>
                        <div className="formRow">
                            <button
                                className='close-btn'
                                style={{ width: '95%' }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setOpenPhotoUpload(false);
                                }}>
                                <span>Close</span>
                            </button>
                            <button className="submitButton"> {loading ? <span className='spinner' /> : 'Upload'}</button>
                        </div>
                    </form>
                </div>

            </Modal >


            {/* Patients */}
            {(activeIndex === 0) &&
                <div className='add-btn-wrapper'>
                    <button className='add-btn' onClick={() => navigate('/hospitalQr')}>Add patient</button>
                </div>
            }

            {(patients?.length === 0) && (activeIndex === 0) && <p><i>You have no patients</i></p>}

            {
                (activeIndex === 0) && patients?.length !== 0 &&

                <>
                    <div className='formRow' style={{ marginTop: '10px' }}>
                        <div className='formGroup'>
                            <input
                                className='formInput'
                                type="text"
                                placeholder='Filter'
                                value={filter}
                                onChange={(event) => setFilter(event.target.value)}
                            />
                        </div>
                        <div className='formGroup'>
                            <button className="add-btn" onClick={fetchFilteredPatients}>Search</button> &nbsp;
                            <button className="close-btn" style={{ borderRadius: '10rem' }} onClick={clearFilter}>Clear filter</button>
                        </div>
                    </div>

                    <section className="user-list">
                        <div className="list-header">
                            <span>Name</span>
                            <span>Gender</span>
                            <span>Age</span>
                            <span>Mobile</span>
                            <span>Admitted on</span>
                            <span>Action</span>
                        </div>

                        <div className="list-body">
                            {
                                (filteredPatients.length !== 0 ? filteredPatients : patients).map((patient) => (
                                    <div className="list-row" key={patient?.addedAt}>
                                        <span className='truncate' title={patient?.patient?.name} data-label="Name">{patient?.patient?.name}</span>
                                        <span className='truncate' title={patient?.patient?.gender} data-label="Gender">{patient?.patient?.gender}</span>
                                        <span className='truncate' title={patient?.patient?.age} data-label="Age">{patient?.patient?.age}</span>
                                        <span className='truncate' title={patient?.patient?.mobile} data-label="Mobile">{patient?.patient?.mobile}</span>
                                        <span className='truncate' title={new Date(patient?.addedAt).toLocaleString('en-In',
                                            // @ts-ignore
                                            admittedAtOptions)} data-label="Admitted on">{new Date(patient?.addedAt).toLocaleString('en-In', admittedAtOptions)}</span>
                                        <span><button className="close-btn" onClick={() => handleDeletePatient(patient?._id)}>Delete</button></span>
                                    </div>
                                ))
                            }
                        </div>

                    </section>

                    {(totalPatients !== patients.length) && (filteredPatients.length === 0) ?
                        <div>
                            <button className='add-btn' disabled={pageLoading} onClick={() => {
                                setLoadPatients(true);
                            }}>{pageLoading ? <span className='spinner' /> : 'Load more'}</button>
                        </div>
                        : <></>}


                    <br />
                </>
            }

            {/* Doctors */}
            {(activeIndex === 1) &&
                <>
                    <div className='add-btn-wrapper'>
                        <button className='add-btn' onClick={() => setOpenDoctor(true)}>Add doctor</button>
                    </div>

                    <Modal
                        isOpen={openDoctor}
                        onRequestClose={() => setOpenDoctor(false)}
                        style={{
                            overlay: { backgroundColor: "rgba(0,0,0,0.4)" },
                        }}
                        className='formContainer'
                    >
                        <div className='formWrapper'>
                            <h2 className='formTitle'>Add Doctor</h2>
                            <form onSubmit={handleAddMember}>

                                <div className='formRow'>

                                    <div className='formGroup'>
                                        <span className='formLabel'>Name</span>
                                        <input
                                            className='formInput'
                                            type="text"
                                            autoFocus
                                            value={name}
                                            onChange={(event) => setName(event.target.value)}
                                            required
                                        />
                                    </div>

                                    <div className='formGroup'>
                                        <span className='formLabel'>Speciality</span>
                                        <input
                                            className='formInput'
                                            type="text"
                                            value={speciality}
                                            onChange={(event) => setSpeciality(event.target.value)}
                                            required
                                        />
                                    </div>

                                </div>

                                <div className='formRow'>
                                    <button
                                        className='close-btn'
                                        style={{ width: '95%' }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setOpenDoctor(false);
                                        }}>
                                        <span>Close</span>
                                    </button>
                                    <button
                                        className='submitButton'
                                        type="submit" disabled={loading}
                                    >
                                        {loading ? <span className='spinner' /> : 'Add'}
                                    </button>
                                </div>
                            </form>
                        </div>

                    </Modal >
                </>
            }

            {(doctors?.length === 0) && (activeIndex === 1) && <p><i>You haven't added any doctors yet :(</i></p>}

            {
                (activeIndex === 1) && doctors?.length !== 0 &&
                <section className="user-list">
                    <div className="list-header">
                        <span>Name</span>
                        <span>Speciality</span>
                        <span>Action</span>
                    </div>

                    <div className="list-body">
                        {
                            doctors.map((doctor) => (
                                <div className="list-row" key={doctor?._id}>
                                    <span className='truncate' title={doctor?.name} data-label="Name">{doctor?.name}</span>
                                    <span className='truncate' title={doctor?.speciality} data-label="Speciality">{doctor?.speciality}</span>
                                    <span><button className="close-btn" onClick={() => {
                                        setDeleteModalData({ subject: doctor?.name, delete: handleDeleteDoctor, id: doctor?._id });
                                        setOpenDeleteModal(true);
                                    }}>Delete</button></span>
                                </div>
                            ))
                        }
                    </div>

                </section>
            }

            <Modal
                isOpen={openDeleteModal}
                onRequestClose={() => setOpenDeleteModal(false)}
                style={{
                    overlay: { backgroundColor: "rgba(0,0,0,0.4)" },
                }}
                className='formContainer'
            >
                <div className='formWrapper'>
                    <h2 className='formTitle'>Do you want to delete&nbsp;
                        {// @ts-ignore
                            deleteModalData?.subject}?</h2>
                    <div className='formRow'>
                        <button
                            className='close-btn'
                            style={{ width: '95%' }}
                            onClick={(e) => {
                                e.preventDefault();
                                setOpenDeleteModal(false);
                                setDeleteModalData({})
                            }}>
                            <span>Close</span>
                        </button>
                        <button className="submitButton" onClick={() => {
                            // @ts-ignore
                            deleteModalData?.delete(deleteModalData?.id);
                            setOpenDeleteModal(false);
                        }}>Delete</button>
                    </div>
                </div>
            </Modal >

        </div>
    )
}

export default HospitalDashboard
